import { Plus } from 'lucide-react'
import type { Scene } from '@shared/types'
import { api } from '../../../api'
import { formatTime } from '../../../lib/time'
import { useEditorStore } from '../../../store/editorStore'
import { useUiStore } from '../../../store/uiStore'
import { SCENE_DRAG_MIME } from '../dragTypes'
import { computeInsertIndexAtPlayhead } from './insertAt'

/**
 * Small dominant-hue swatch (task: "章の色は色相で自動割当" — REVIEW_v3.md
 * M-E). `scene.hue` is measured at ingest (server/src/ingest/hue.ts) and
 * only present once that measurement has run; older assets ingested before
 * this feature (or a fully desaturated scene) have no dot at all rather
 * than a misleading gray/black one. Saturation is floored a bit for the
 * swatch itself so a dim/dark scene's hue is still legible as a color chip.
 */
function HueDot({ scene }: { scene: Scene }) {
  if (scene.hue === undefined) return null
  const sat = Math.round(Math.max(scene.sat ?? 0.6, 0.35) * 100)
  const lightness = 50
  return (
    <div
      className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full border border-white/70 shadow-sm"
      style={{ backgroundColor: `hsl(${Math.round(scene.hue)}, ${sat}%, ${lightness}%)` }}
      title={`hue ${Math.round(scene.hue)}°`}
      data-testid="scene-chip-hue-dot"
    />
  )
}

export function SceneChip({ assetId, scene }: { assetId: string; scene: Scene }) {
  const dur = scene.endSec - scene.startSec
  const addSegmentFromScene = useEditorStore((s) => s.addSegmentFromScene)
  const setHoverAssetId = useUiStore((s) => s.setHoverAssetId)

  function handleAdd(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    // UI_MK2_PHASE1.md §3-3: plain click appends to the end (`insertAtIdx`
    // omitted); ⌥(Alt)+click inserts right after the Segment the playhead
    // is currently over.
    // UI_MK2_PHASE2.md §2-7 (reviewer minor): `playback.currentSec` を購読
    // すると再生中は毎フレーム再レンダーされる — クリック時だけ getState() で
    // 読めば十分。
    const store = useEditorStore.getState()
    const insertAtIdx = e.altKey
      ? computeInsertIndexAtPlayhead(store.composition?.tracks.video ?? [], store.playback.currentSec)
      : undefined
    addSegmentFromScene(assetId, scene, insertAtIdx)
  }

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(SCENE_DRAG_MIME, JSON.stringify({ assetId, sceneIndex: scene.index }))
        e.dataTransfer.effectAllowed = 'copy'
      }}
      onMouseEnter={() => setHoverAssetId(assetId)}
      onMouseLeave={() => setHoverAssetId(null)}
      data-testid="scene-chip"
      data-asset-id={assetId}
      data-scene-index={scene.index}
      className="group relative w-[72px] h-[128px] rounded overflow-hidden shrink-0 cursor-grab active:cursor-grabbing border border-ink-border hover:border-accent-blue/50 bg-ink-bg"
      title={`${formatTime(scene.startSec)} 〜 ${formatTime(scene.endSec)}`}
    >
      {scene.thumbPath ? (
        <img src={api.fileUrl(scene.thumbPath)} alt="" className="w-full h-full object-cover pointer-events-none" />
      ) : (
        <div className="w-full h-full bg-ink-surface2" />
      )}
      <HueDot scene={scene} />
      <div className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] text-white text-center py-0.5 tabular-nums">
        {dur.toFixed(1)}s
      </div>
      <button
        onClick={handleAdd}
        title="タイムライン末尾に追加（⌥＋クリックで再生ヘッド位置に挿入）"
        data-testid="scene-chip-add"
        className="absolute top-1 left-1 w-5 h-5 rounded-full bg-black/70 border border-white/30 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent-blue/80 hover:border-accent-blue"
      >
        <Plus size={12} />
      </button>
    </div>
  )
}
